"use client";
import React from "react";
import { LiveChatButton, ButtonCta } from "@/components";

const floatCovers = [
  { src: "/images/book-covers/hero-slide-4.webp", cls: "left-[2%] top-[18%] w-28 md:w-36 rotate-[-8deg] [animation-delay:0s]" },
  { src: "/images/book-covers/hero-slide-8.webp", cls: "left-[8%] bottom-[10%] w-24 md:w-32 rotate-[6deg] [animation-delay:1.2s]" },
  { src: "/images/book-covers/hero-slide-11.webp", cls: "right-[4%] top-[14%] w-28 md:w-36 rotate-[7deg] [animation-delay:0.6s]" },
  { src: "/images/book-covers/hero-slide-2.webp", cls: "right-[9%] bottom-[12%] w-24 md:w-32 rotate-[-6deg] [animation-delay:1.8s]" },
];

function HeroSection({ title, description }) {
  return (
    <section className="relative min-h-[720px] w-full overflow-hidden bg-ink text-white md:min-h-[820px]">
      {/* Cinematic video background */}
      <video
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/images/banner.webp"
      >
        <source src="/images/hero-bg.mp4" type="video/mp4" />
      </video>

      {/* Overlays for legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />

      {/* Floating book covers */}
      {floatCovers.map((c, i) => (
        <img loading="lazy" decoding="async"
          key={i}
          src={c.src}
          alt=""
          aria-hidden
          className={`pointer-events-none absolute z-10 hidden rounded-lg shadow-2xl ring-1 ring-white/10 animate-float-slow lg:block ${c.cls}`}
        />
      ))}

      {/* Content */}
      <div className="relative z-20 mx-auto flex min-h-[720px] max-w-[1350px] flex-col justify-center px-5 py-28 pt-48 text-center md:min-h-[820px] lg:py-32 lg:pt-48">
        <span className="eyebrow mx-auto flex items-center gap-3 text-gold-soft" data-aos="fade-up">
          <span className="h-px w-8 bg-gold/70" />
          Writing • Editing • Design • Global Distribution • Marketing
          <span className="h-px w-8 bg-gold/70" />
        </span>

        <h1
          className="mx-auto mt-6 max-w-4xl font-display text-[clamp(2rem,6vw,4.6rem)] font-semibold leading-[1.06]"
          data-aos="fade-up"
          data-aos-delay="80"
        >
          {title}
        </h1>

        <p
          className="mx-auto mt-6 max-w-2xl text-[15px] leading-relaxed text-white/80 md:text-lg"
          data-aos="fade-up"
          data-aos-delay="150"
        >
          {description}
        </p>

        <div
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
          data-aos="fade-up"
          data-aos-delay="220"
        >
          <ButtonCta
            buttonText="Book a Free Consultation"
            classStyle="text-ink bg-gold hover:bg-gold-soft h-12"
          />
          <LiveChatButton
            buttonText="Talk to a Publishing Advisor"
            classStyle="text-white bg-white/10 border border-white/30 backdrop-blur hover:bg-white/20 h-12"
          />
        </div>

        {/* rating chip */}
        <div
          className="mx-auto mt-12 flex max-w-full flex-col flex-wrap items-center justify-center gap-4 rounded-2xl border border-white/15 bg-white/5 px-6 py-5 backdrop-blur-md sm:flex-row sm:gap-x-8"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          <div className="flex items-center gap-2">
            <span className="flex text-gold-soft">
              {"★★★★★".split("").map((s, i) => (
                <span key={i}>{s}</span>
              ))}
            </span>
            <span className="text-sm text-white/80">4.9/5 Author Rating</span>
          </div>
          <span className="hidden h-6 w-px bg-white/20 sm:block" />
          <div className="text-sm text-white/80">
            <span className="font-display text-lg font-semibold text-white">500+</span> Titles
            Published
          </div>
          <span className="hidden h-6 w-px bg-white/20 sm:block" />
          <div className="text-sm text-white/80">
            <span className="font-display text-lg font-semibold text-white">12+</span> Genres
            Covered
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2 animate-float text-white/60">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M6 13l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </section>
  );
}

export default HeroSection;
